import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import DataTable, { DataTableColumn, DataTableRow } from "../../Widgets/DataTable";
import { InterventionHistoryFormValues } from "./interface";
import InterventionModal, { InterventionModalSaveValues } from "./InterventionModal";
import RowOptions from "../../Widgets/RowOptions";
import { useGetClientById } from "hooks/api/useGetClientById";
import { useUpdateClient } from "hooks/api/client/useUpdateClient";
import useGetInterventionHistory from "hooks/api/client/useGetInterventionHistory";
import useCreateInterventionHistory from "hooks/api/client/useCreateInterventionHistory";
import useUpdateInterventionHistory from "hooks/api/client/useUpdateInterventionHistory";
import useDeleteInterventionHistory from "hooks/api/client/useDeleteInterventionHistory";

const SERVICES_COLUMNS: DataTableColumn[] = [
  { key: "servicesProvided", label: "Services Provided" },
  { key: "interventionProvider", label: "Intervention Provider" },
  { key: "startDate", label: "Start Date" },
  { key: "endDate", label: "End Date" },
  { key: "servicesOngoing", label: "Services Ongoing" },
  { key: "perceivedEffectiveness", label: "Perceived Effectiveness" },
  { key: "actions", label: "" },
];

const ONGOING_LABELS: Record<string, string> = {
  YES: "Yes",
  NO: "No",
  UNKNOWN: "Unknown",
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const EMPTY_VALUES: InterventionHistoryFormValues = {
  hasReceivedServices: "",
  previousServicesDiscontinuationReason: "",
  accessBarriers: [],
};

export const InterventionHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { data: clientData, refetch: refetchClient } = useGetClientById(clientIdNum);
  const { updateClient: updateClientMutation } = useUpdateClient({
    onSuccess: () => {
      refetchClient();
      setEditMode(false);
    },
  });

  const { interventionHistory, refetchInterventionHistory } = useGetInterventionHistory(clientIdNum);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const { createInterventionHistoryMutation, isCreateInterventionHistoryLoading } = useCreateInterventionHistory({
    onSuccess: () => { refetchInterventionHistory(); closeModal(); },
  });

  const { updateInterventionHistoryMutation, isUpdateInterventionHistoryLoading } = useUpdateInterventionHistory({
    onSuccess: () => { refetchInterventionHistory(); closeModal(); },
  });

  const { deleteInterventionHistoryMutation } = useDeleteInterventionHistory({
    onSuccess: () => { refetchInterventionHistory(); },
  });

  const [editMode, setEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DataTableRow | null>(null);
  const [formValues, setFormValues] = useState<InterventionHistoryFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<InterventionHistoryFormValues>(EMPTY_VALUES);

  // Load accessBarriers from client model
  useEffect(() => {
    const client = clientData?.data;
    if (client) {
      setFormValues((prev) => ({
        ...prev,
        accessBarriers: client.barriersToAccess || [],
        previousServicesDiscontinuationReason: client.previousServicesDiscontinuationReason || "",
      }));
      setInitialValues((prev) => ({
        ...prev,
        accessBarriers: client.barriersToAccess || [],
        previousServicesDiscontinuationReason: client.previousServicesDiscontinuationReason || "",
      }));
    }
  }, [clientData?.data]);

  // Load hasReceivedServices from first intervention history row (receivedIntervention field)
  useEffect(() => {
    const receivedIntervention = (interventionHistory[0] as any)?.receivedIntervention || "";
    setFormValues((prev) => ({ ...prev, hasReceivedServices: receivedIntervention }));
    setInitialValues((prev) => ({ ...prev, hasReceivedServices: receivedIntervention }));
  }, [interventionHistory]);

  const handleChange =
    (field: keyof InterventionHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleMultiChange =
    (field: keyof InterventionHistoryFormValues) =>
    (value: string[]) => {
      setFormValues((prev) => ({ ...prev, [field]: value }));
    };

  const handleSave = () => {
    if (!clientId) return;

    // Save barriersToAccess on the client model
    updateClientMutation({
      clientId,
      data: {
        barriersToAccess: formValues.accessBarriers,
        previousServicesDiscontinuationReason:
          formValues.hasReceivedServices === "YES"
            ? formValues.previousServicesDiscontinuationReason
            : "",
      },
    });

    // Save hasReceivedServices on the first intervention history row (if any)
    if (interventionHistory.length > 0) {
      const firstRow = interventionHistory[0] as any;
      updateInterventionHistoryMutation({
        id: Number(firstRow.id),
        payload: { receivedIntervention: formValues.hasReceivedServices || null },
      });
    }
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const handleModalSave = (values: InterventionModalSaveValues) => {
    if (!clientIdNum) return;
    const payload = {
      serviceProvided: values.servicesProvided || null,
      startDate: values.startDate ? values.startDate.toISOString() : null,
      endDate: values.endDate ? values.endDate.toISOString() : null,
      ongoing: values.ongoing || null,
      perceivedEffectiveness: values.perceivedEffectiveness || null,
      providerName: values.providerName || null,
      // Carry receivedIntervention on every row so the first-row read is always up-to-date
      receivedIntervention: formValues.hasReceivedServices || null,
    };

    if (selectedRow) {
      updateInterventionHistoryMutation({ id: Number(selectedRow.id), payload });
    } else {
      createInterventionHistoryMutation({ clientId: clientIdNum, payload });
    }
  };

  const saveDisabled = JSON.stringify(formValues) === JSON.stringify(initialValues);

  const rows: DataTableRow[] = interventionHistory.map((item: any) => ({
    id: String(item.id),
    servicesProvided: item.serviceProvided || "-",
    interventionProvider: item.providerName || "-",
    startDate: formatDate(item.startDate),
    endDate: formatDate(item.endDate),
    servicesOngoing: ONGOING_LABELS[item.ongoing] || item.ongoing || "-",
    perceivedEffectiveness: item.perceivedEffectiveness || "-",
    _serviceProvided: item.serviceProvided || "",
    _startDate: item.startDate || null,
    _endDate: item.endDate || null,
    _ongoing: item.ongoing || "",
    _perceivedEffectiveness: item.perceivedEffectiveness || "",
    _providerName: item.providerName || "",
  }));

  return (
    <SectionWrapper
      title="Intervention History"
      description="Past and Current Intervention Records"
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} handleMultiChange={handleMultiChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}

      <DataTable
        columns={SERVICES_COLUMNS}
        rows={rows}
        ariaLabel="Intervention history"
        emptyMessage="No intervention records"
        onAddNew={() => { setSelectedRow(null); setModalOpen(true); }}
        sx={{ mt: 7 }}
        renderCell={(col, row) => {
          if (col.key === "actions") {
            return (
              <RowOptions
                onEdit={() => { setSelectedRow(row); setModalOpen(true); }}
                onDelete={() => deleteInterventionHistoryMutation(Number(row.id))}
              />
            );
          }
          return row[col.key] || "-";
        }}
      />

      <InterventionModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleModalSave}
        clientId={clientId}
        isSaving={isCreateInterventionHistoryLoading || isUpdateInterventionHistoryLoading}
        initialValues={selectedRow ? {
          servicesProvided: selectedRow._serviceProvided as string,
          startDate: selectedRow._startDate ? new Date(selectedRow._startDate as string) : null,
          endDate: selectedRow._endDate ? new Date(selectedRow._endDate as string) : null,
          ongoing: selectedRow._ongoing as string,
          perceivedEffectiveness: selectedRow._perceivedEffectiveness as string,
          providerName: selectedRow._providerName as string,
        } : undefined}
      />
    </SectionWrapper>
  );
};

export default InterventionHistory;
